import React, { useEffect, useRef } from "react";

const MAX_REPORT_LABEL_LENGTH = 50;

const normalizeReportLabelText = (value: string): string => {
  return value.replace(/\u00a0/g, " ").replace(/\r\n/g, "\n");
};

interface ReportLabelInputProps {
  value: string;
  editable: boolean;
  onChange: (value: string) => void;
  onBlur: (value: string) => void;
}

export const ReportLabelInput: React.FC<ReportLabelInputProps> = ({
  value,
  editable,
  onChange,
  onBlur,
}) => {
  const reportLabelRef = useRef<HTMLDivElement | null>(null);
  const focusedValueRef = useRef<string>(value);

  useEffect(() => {
    const reportLabelElement = reportLabelRef.current;

    if (!reportLabelElement) {
      return;
    }

    const normalizedValue = normalizeReportLabelText(value);

    if (normalizeReportLabelText(reportLabelElement.textContent ?? "") !== normalizedValue) {
      reportLabelElement.textContent = normalizedValue;
    }
  }, [value]);

  const getSelectedTextLength = (): number => {
    return window.getSelection()?.toString().length ?? 0;
  };

  const handleBeforeInput = (event: React.FormEvent<HTMLDivElement>): void => {
    const nativeEvent = event.nativeEvent as InputEvent;
    const currentValue = normalizeReportLabelText(event.currentTarget.textContent ?? "");
    const selectedTextLength = getSelectedTextLength();
    let insertedText = nativeEvent.data ?? "";

    if (
      nativeEvent.inputType === "insertParagraph" ||
      nativeEvent.inputType === "insertLineBreak"
    ) {
      insertedText = "\n";
    }

    if (!insertedText) {
      return;
    }

    if (currentValue.length - selectedTextLength + insertedText.length > MAX_REPORT_LABEL_LENGTH) {
      event.preventDefault();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>): void => {
    const pastedText = normalizeReportLabelText(event.clipboardData.getData("text"));
    const currentValue = normalizeReportLabelText(event.currentTarget.textContent ?? "");
    const selectedTextLength = getSelectedTextLength();

    if (currentValue.length - selectedTextLength + pastedText.length > MAX_REPORT_LABEL_LENGTH) {
      event.preventDefault();
    }
  };

  const handleInput = (event: React.FormEvent<HTMLDivElement>): void => {
    onChange(normalizeReportLabelText(event.currentTarget.textContent ?? ""));
  };

  const handleFocus = (event: React.FocusEvent<HTMLDivElement>): void => {
    focusedValueRef.current = normalizeReportLabelText(event.currentTarget.textContent ?? "");
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>): void => {
    const changedReportLabel = normalizeReportLabelText(event.currentTarget.textContent ?? "");

    if (changedReportLabel !== focusedValueRef.current) {
      onBlur(changedReportLabel);
    }
  };

  return (
    <div
      ref={reportLabelRef}
      className="report-label-input"
      contentEditable={editable}
      suppressContentEditableWarning
      role="textbox"
      aria-multiline="true"
      onBeforeInput={handleBeforeInput}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onInput={handleInput}
      onPaste={handlePaste}
    />
  );
};
